import { z } from 'zod'
import { BRAND_ASSET_KEYS, FAQ_GROUPS, LINK_KEYS, UPLOAD_CATEGORIES } from './catalogs'
import { StringField, fieldOf } from './field'
import { Slug } from './ids'
import { ACCESS_STATUSES, PLATFORM_KEYS } from './platforms'
import { DAYS } from './schedule'

const F = StringField

const TimeRange = z.object({ open: z.string(), close: z.string() })
const DayHours = z.object({ closed: z.boolean(), ranges: z.array(TimeRange) })
export const ScheduleSchema = z.object({
  id: z.string(),
  label: z.string(),
  days: z.object(Object.fromEntries(DAYS.map((d) => [d, DayHours])) as Record<(typeof DAYS)[number], typeof DayHours>),
})

export const BrandItem = z.object({
  name: F, legal: F, industry: F, background: F, website: F, tone: F, themes: F, avoid: F,
  createdAt: z.number(),
})
/** `notes` is the team's kickoff notes for the whole client. The client's portal never shows it. */
export const BrandsSection = z.object({ order: z.array(Slug), items: z.record(Slug, BrandItem), notes: F.optional() })

export const LocationItem = z.object({
  brandId: Slug,
  name: F, address: F, phone: F, extra: F,
  /** Announced but not open yet: hours are not required, and `opening` says when. Staff set it. */
  upcoming: z.boolean().default(false),
  opening: F.optional(),
  timezone: z.string().default('America/New_York'),
  schedules: fieldOf(z.array(ScheduleSchema)),
  createdAt: z.number(),
})
export const LocationsSection = z.object({ order: z.array(Slug), items: z.record(Slug, LocationItem) })

export const AudienceItem = z.object({ age: F, regulars: F, grow: F, occasions: F, price: F, neighborhoods: F })
export const AudienceSection = z.object({ items: z.record(Slug, AudienceItem) })

export const AccountItem = z.object({
  brandId: Slug,
  platform: z.enum(PLATFORM_KEYS),
  handle: F,
  status: fieldOf(z.enum(ACCESS_STATUSES.map((s) => s.value) as [string, ...string[]])),
  login: F,
  /** secrets/{id}; only the setSecret callable writes this. */
  secretRef: z.string().nullable().default(null),
  notes: F,
})
export const SocialSection = z.object({ items: z.record(z.string(), AccountItem) })

export const DriveItem = z.object({ label: F, url: F, access: F })
export const BrandKitItem = z.object({ link: F, notes: F })
export const UploadMeta = z.object({
  name: z.string(), storagePath: z.string(), size: z.number(), contentType: z.string(),
  brandId: Slug.nullable().default(null), assetKey: z.string().nullable().default(null),
  /** Set when someone files it under a category; until then it is guessed from the file type. */
  category: z.enum(UPLOAD_CATEGORIES.map((c) => c.key) as [string, ...string[]]).nullable().default(null),
  /** A short label anyone can add, e.g. "Primary logo" or "White version for dark backgrounds". */
  note: z.string().nullable().default(null),
  uploadedBy: z.string(), at: z.number(),
})
export const AssetsSection = z.object({
  drives: z.record(z.string(), DriveItem),
  brandKit: z.record(Slug, z.partialRecord(z.enum(BRAND_ASSET_KEYS.map((a) => a.key) as [string, ...string[]]), BrandKitItem)),
  uploads: z.record(z.string(), UploadMeta),
})

export const PromotionSchema = z.object({
  id: z.string(),
  name: z.string(),
  details: z.string(),
  repeat: z.enum(['weekly', 'once']),
  days: z.array(z.enum(DAYS)),
  date: z.string().nullable(),
  allDay: z.boolean(),
  start: z.string().nullable(),
  end: z.string().nullable(),
})
export const LinksSection = z.object({
  keyLinks: z.record(Slug, z.partialRecord(z.enum(LINK_KEYS.map((l) => l.key) as [string, ...string[]]), F)),
})
export const SpecialsSection = z.object({
  /** Specials and promotions per location, confirmed as a whole like a location's hours. */
  promotions: z.record(Slug, fieldOf(z.array(PromotionSchema))).default({}),
  /** Recurring events, seasonal moments and milestones across the whole client. */
  events: F,
})

/** `contact` and `email` are older free-text routing; contacts below replace them. `answer` here is from before answers were per brand. */
export const FaqAnswer = z.object({ answer: F, contact: F, email: F })
/**
 * One brand's answer to one question. `split` means it differs by location: `locations` holds one answer
 * per location of the brand and `answer` is set aside until it is switched back. `contacts` are the
 * contacts (ids in the contacts section) chosen to handle it; a contact's topic tags only suggest them.
 */
export const BrandFaqAnswer = z.object({
  answer: F,
  split: z.boolean().default(false),
  locations: z.record(Slug, F).default({}),
  contacts: z.array(z.string()).default([]),
})
/**
 * A phone number or inbox and what it's for. `topics` (CONTACT_TOPICS ids) say what it handles and tie it to
 * FAQ questions and a location's main line; `name` and `role` are who picks up, blank for a shared line.
 * `locationIds` empty means every location.
 */
export const ContactItem = z.object({
  name: F, role: F, phone: F, email: F,
  topics: z.array(z.string()).default([]),
  locationIds: z.array(Slug).default([]),
  createdAt: z.number(),
})
export const ContactsSection = z.object({ items: z.record(z.string(), ContactItem) })
/** A question asked only of this client, alongside the standard ones in the faqQuestions collection. */
export const CustomFaqQuestion = z.object({ label: z.string(), group: z.enum(FAQ_GROUPS), order: z.number(), createdAt: z.number() })
export const FaqSection = z.object({
  answers: z.record(z.string(), FaqAnswer),
  /** Question id (prefixed "custom-") → this client's own questions. */
  custom: z.record(z.string(), CustomFaqQuestion).default({}),
  /** Standard question id → wording used for this client only, in place of the shared wording. */
  labels: z.record(z.string(), z.string()).default({}),
  /** Standard question id → group it's listed under for this client only. */
  groups: z.record(z.string(), z.enum(FAQ_GROUPS)).default({}),
  /** brandId → question id → answer. */
  brands: z.record(Slug, z.record(z.string(), BrandFaqAnswer)).default({}),
})


export const SECTION_SCHEMAS = {
  brands: BrandsSection,
  locations: LocationsSection,
  specials: SpecialsSection,
  contacts: ContactsSection,
  audience: AudienceSection,
  social: SocialSection,
  assets: AssetsSection,
  links: LinksSection,
  faq: FaqSection,
} as const

export type BrandsSection = z.infer<typeof BrandsSection>
export type LocationsSection = z.infer<typeof LocationsSection>
export type AudienceSection = z.infer<typeof AudienceSection>
export type SocialSection = z.infer<typeof SocialSection>
export type AssetsSection = z.infer<typeof AssetsSection>
export type LinksSection = z.infer<typeof LinksSection>
export type SpecialsSection = z.infer<typeof SpecialsSection>
export type FaqSection = z.infer<typeof FaqSection>
export type ContactsSection = z.infer<typeof ContactsSection>

export type SectionDocs = {
  brands: BrandsSection
  locations: LocationsSection
  specials: SpecialsSection
  contacts: ContactsSection
  audience: AudienceSection
  social: SocialSection
  assets: AssetsSection
  links: LinksSection
  faq: FaqSection
}

/** Empty documents seeded by onClientCreated so the UI never handles absent docs. */
export function emptySections(): SectionDocs {
  return {
    brands: { order: [], items: {} },
    locations: { order: [], items: {} },
    specials: { promotions: {}, events: { value: '', prefilled: false, confirmed: false, source: 'staff', updatedBy: 'system', updatedAt: 0 } },
    contacts: { items: {} },
    audience: { items: {} },
    social: { items: {} },
    assets: { drives: {}, brandKit: {}, uploads: {} },
    links: { keyLinks: {} },
    faq: { answers: {}, custom: {}, labels: {}, groups: {}, brands: {} },
  }
}
